export function createHighPassFilter(audioContext: AudioContext, type: 'bell' | 'diaphragm'): BiquadFilterNode {
  const highPassFilter = audioContext.createBiquadFilter()
  highPassFilter.type = 'highpass'
  highPassFilter.frequency.value = type === 'bell' ? 20 : 100 // Bell: 20 Hz, Diaphragm: 100 Hz
  return highPassFilter
}

export function createLowPassFilter(audioContext: AudioContext, type: 'bell' | 'diaphragm'): BiquadFilterNode {
  const lowPassFilter = audioContext.createBiquadFilter()
  lowPassFilter.type = 'lowpass'
  lowPassFilter.frequency.value = type === 'bell' ? 300 : 1000 // Bell: 300 Hz, Diaphragm: 1000 Hz
  return lowPassFilter
}

export function createBandPassFilter(audioContext: AudioContext, type: 'bell' | 'diaphragm'): BiquadFilterNode {
  const bandPassFilter = audioContext.createBiquadFilter()
  bandPassFilter.type = 'bandpass'

  if (type === 'bell') {
    bandPassFilter.frequency.value = 150 // Center frequency for bell mode
    bandPassFilter.Q.value = 1 // Quality factor
  } else {
    bandPassFilter.frequency.value = 550 // Center frequency for diaphragm mode
    bandPassFilter.Q.value = 0.8 // Quality factor adjusted to cover the range 100-1000 Hz
  }
  return bandPassFilter
}

// General helpers shared across services
export function normalizeLabel(input?: string): string {
  return (input ?? '').toLowerCase().trim()
}

export function getFirstAudioTrackLabel(stream: MediaStream): string {
  return stream.getAudioTracks()[0]?.label || ''
}

export function createAudioProcessingChain(
  audioContext: AudioContext,
  filterType: 'bell' | 'diaphragm' | 'raw',
  stream: MediaStream
): MediaStreamAudioDestinationNode {
  const source = audioContext.createMediaStreamSource(stream)
  const destination: MediaStreamAudioDestinationNode = audioContext.createMediaStreamDestination()

  switch (filterType) {
    case 'bell':
      {
        const highPassFilter = audioContext.createBiquadFilter()
        highPassFilter.type = 'highpass'
        highPassFilter.frequency.value = 20 // High-pass filter frequency
        highPassFilter.Q.value = 0.7 // Division as per the original obfuscated logic (not used here effectively)

        const peakingFilter1 = audioContext.createBiquadFilter()
        peakingFilter1.type = 'peaking'
        peakingFilter1.frequency.value = 150 // Peaking filter 1 frequency
        peakingFilter1.Q.value = 10 // Peaking filter 1 Q factor
        peakingFilter1.gain.value = 6 // Peaking filter 1 gain

        const lowPassFilter = audioContext.createBiquadFilter()
        lowPassFilter.type = 'lowpass'
        lowPassFilter.frequency.value = 300 // Low-pass filter frequency
        lowPassFilter.Q.value = 0.7 // Low Q factor for a smooth roll-off

        const lowBoostFilter = audioContext.createBiquadFilter()
        lowBoostFilter.type = 'lowshelf'
        lowBoostFilter.frequency.value = 400 // Low-boost filter frequency
        lowBoostFilter.gain.value = 5 // Low-boost filter gain

        source.connect(highPassFilter).connect(peakingFilter1).connect(lowPassFilter).connect(lowBoostFilter).connect(destination)
      }
      break

    case 'diaphragm':
      {
        // High-pass filter to remove low-frequency noise
        const highPassFilter = audioContext.createBiquadFilter()
        highPassFilter.type = 'highpass'
        highPassFilter.frequency.value = 100 // Adjust this value as needed
        highPassFilter.Q.value = 1.0

        // Low-pass filter to remove high-frequency noise
        const lowPassFilter = audioContext.createBiquadFilter()
        lowPassFilter.type = 'lowpass'
        lowPassFilter.frequency.value = 2000 // Adjust this value as needed
        lowPassFilter.Q.value = 1.0

        // Peaking filter to emphasize the lung sounds
        const peakingFilter1 = audioContext.createBiquadFilter()
        peakingFilter1.type = 'peaking'
        peakingFilter1.frequency.value = 300
        peakingFilter1.Q.value = 5
        peakingFilter1.gain.value = 6

        // Compressor to manage dynamic range
        const compressor = audioContext.createDynamicsCompressor()
        compressor.threshold.setValueAtTime(-30, audioContext.currentTime) // dB, adjust as needed
        compressor.knee.setValueAtTime(40, audioContext.currentTime) // dB, adjust as needed
        compressor.ratio.setValueAtTime(12, audioContext.currentTime) // Adjust as needed
        compressor.attack.setValueAtTime(0, audioContext.currentTime) // Seconds, adjust as needed
        compressor.release.setValueAtTime(0.25, audioContext.currentTime) // Seconds, adjust as needed

        source.connect(highPassFilter).connect(lowPassFilter).connect(peakingFilter1).connect(compressor).connect(destination)
      }
      break

    case 'raw':
      source.connect(destination)
      break

    default:
      throw new Error('Unknown filter type')
  }

  return destination
}
